/**
 * Single source of truth for all public-page SEO / GEO metadata.
 *
 * Plain CommonJS on purpose, so it can be used by BOTH:
 *   - the React app            (import { ROUTES } from "./seoData")
 *   - the Node post-build step (require("../src/seoData"))  -> scripts/seo-postbuild.js
 *
 * When you change a title / description here, the page <head>, the pre-rendered
 * HTML, sitemap.xml and the structured data all stay in sync.
 *
 * Keep titles <= 60 characters and descriptions <= 160 characters
 * (checked by `node scripts/seo-postbuild.js --check`).
 */

const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://lmsbots.com").replace(/\/$/, "");

const SITE = {
  name: "LMSBOTS",
  legalName: "LMSBOTS Pvt. Ltd.",
  url: SITE_URL,
  tagline: "Automate. Convert. Thrive.",
  email: "info@deetyasoft.com",
  telephone: "+91-9015350505",
  address: {
    streetAddress: "FF-06, First Floor, Mahaluxmi Square, Plot No. C-1, Abhay Khand-II, Indirapuram",
    addressLocality: "Ghaziabad",
    addressRegion: "Uttar Pradesh",
    postalCode: "201014",
    addressCountry: "IN",
  },
  // Cities / regions you actively sell to. Used for areaServed (GEO / local relevance).
  areaServed: ["India", "Ghaziabad", "Delhi NCR", "Noida", "Gurugram"],
  sameAs: [
    "https://www.linkedin.com/company/lmsbots",
    "https://twitter.com/lmsbots",
    "https://www.facebook.com/lmsbots",
    "https://www.instagram.com/lmsbots",
  ],
  ogImage: "/og-image.png",
  logo: "/logo512.png",
  lastmod: "2026-10-26", // bump when page content meaningfully changes
};

const FAQS = [
  {
    question: "What is a Lead Management System and how does it work?",
    answer:
      "A Lead Management System is a platform that helps businesses capture, track, and manage potential customers (leads) from multiple sources. It works by collecting leads from forms, ads, emails, and other channels, organizing them in one place, and allowing teams to assign, nurture, and convert them into paying customers efficiently.",
  },
  {
    question: "How can a Lead Management System improve sales performance?",
    answer:
      "A Lead Management System improves sales performance by automatically capturing leads, assigning them to the right sales agents, tracking follow-ups, sending reminders, and analyzing lead activity. This ensures no lead is missed and helps sales teams focus on high-quality prospects that are more likely to convert.",
  },
  {
    question: "Can I customize the lead workflow in the system?",
    answer:
      "Yes, LMSBOTS lets you customize workflows according to your business needs. You can create custom lead stages, automate follow-ups, assign leads to specific teams, set priority levels, and design workflows that match your sales process.",
  },
  {
    question: "Is a Lead Management System suitable for small businesses?",
    answer:
      "Yes, LMSBOTS is designed for businesses of all sizes. Small businesses can use it to organize leads, track customer interactions, and manage follow-ups effectively, while larger businesses can scale the system to handle thousands of leads and multiple sales teams.",
  },
  {
    question: "Can the system capture leads from multiple sources?",
    answer:
      "Yes, LMSBOTS can capture leads from various sources such as website forms, landing pages, social media campaigns, email marketing, ads, and third-party integrations. This helps businesses manage all leads from a single dashboard.",
  },
  {
    question: "Does the system provide lead tracking and analytics?",
    answer:
      "Yes, LMSBOTS includes analytics and reporting features. You can track lead sources, monitor conversion rates, analyze sales team performance, and gain insights that help improve your marketing and sales strategies.",
  },
];

/**
 * Per-route metadata.
 *  name        short label (breadcrumbs, nav, llms.txt)
 *  group       "main" | "solution" | "legal"  (breadcrumb + llms.txt grouping)
 *  heading     <h1> used in the pre-rendered fallback (should match the page's visible h1 intent)
 *  summary     short factual paragraphs shown to crawlers that do not run JavaScript
 *  points      bullet list for the same purpose
 */
const ROUTES = {
  "/": {
    name: "Home",
    group: "main",
    title: "LMSBOTS | AI Lead Management System & CRM for Sales Teams",
    description:
      "LMSBOTS is a lead management CRM that captures leads from every channel, auto-assigns reps, automates follow-ups and tracks conversions in one dashboard.",
    heading: "AI Lead Management System & CRM for Sales Teams",
    summary: [
      "LMSBOTS is a cloud lead management system and CRM that helps sales teams capture leads from every channel, assign them to the right sales rep automatically, automate follow-ups and track conversions from a single dashboard and mobile app.",
      "It is built and supported from Indirapuram, Ghaziabad (Delhi NCR), India, and serves businesses across India in healthcare, education, real estate, insurance, banking & finance and technology.",
    ],
    points: [
      "Omnichannel lead capture: website forms, landing pages, social campaigns, email, ads and third-party integrations",
      "Automatic lead assignment and routing to sales agents",
      "Automated follow-ups, reminders and WhatsApp / SMS / email nurturing",
      "Centralised activity timeline: calls, notes, tasks and status updates",
      "Pipeline, source and team-performance analytics",
      "Pricing: ₹499 per user per month, or ₹399 per user per month billed annually; 14-day free trial, no credit card required",
    ],
    priority: "1.0",
    changefreq: "weekly",
  },
  "/features": {
    name: "Features",
    group: "main",
    title: "CRM & Lead Management Features | LMSBOTS",
    description:
      "Explore LMSBOTS features: lead capture, scoring, auto-assignment, WhatsApp automation, pipeline management, analytics, reports and team collaboration.",
    heading: "Features That Drive Growth",
    summary: [
      "LMSBOTS combines lead capture, scoring, assignment, nurturing, pipeline management, analytics and WhatsApp automation in one CRM.",
    ],
    points: [
      "Lead Capture & Tracking, Smart Lead Scoring, Lead Segmentation",
      "Automated Lead Nurturing and Automated Lead Assignment",
      "Sales Pipeline Management, Task & Reminder System, Smart Notifications & Alerts",
      "AI Sales Assistant",
      "Lead Analytics Dashboard and Custom Reports",
      "WhatsApp Integration and CRM & Tool Integrations",
      "Team Collaboration, Enterprise Security, 24/7 Customer Support",
    ],
    priority: "0.9",
    changefreq: "monthly",
  },
  "/pricing": {
    name: "Pricing",
    group: "main",
    title: "LMSBOTS Pricing | CRM from ₹399/user/month",
    description:
      "LMSBOTS CRM pricing: ₹499 per user per month, or ₹399 per user per month billed annually (save 20%). 14-day free trial, no credit card required.",
    heading: "Simple, Transparent Pricing",
    summary: [
      "LMSBOTS uses simple per-user pricing: ₹499 per user per month billed monthly, or ₹399 per user per month billed annually (a 20% saving). Every plan includes a 14-day free trial with all features enabled and no credit card required.",
    ],
    points: [
      "Per-user pricing — pay only for active team members",
      "Centralised lead dashboard, automated lead assignment, follow-up reminders and notifications",
      "Reports and performance analytics, lead source tracking, activity timeline",
      "WhatsApp automation included (Meta's standard conversation charges apply, with no LMSBOTS markup)",
      "Priority email support; cancel any time",
    ],
    priority: "0.9",
    changefreq: "monthly",
  },
  "/about-us": {
    name: "About Us",
    group: "main",
    title: "About LMSBOTS — Smart Lead Management & CRM Software",
    description:
      "LMSBOTS is a lead management and CRM platform that helps sales teams capture leads, automate follow-ups and convert faster. Meet the team and our mission.",
    heading: "About LMSBOTS",
    summary: [
      "LMSBOTS (LMSBOTS Pvt. Ltd.) builds lead management and CRM software that helps modern sales teams capture leads, automate follow-ups and convert faster. The company is based in Indirapuram, Ghaziabad, Uttar Pradesh, India.",
    ],
    points: [],
    priority: "0.7",
    changefreq: "monthly",
  },
  "/contact": {
    name: "Contact",
    group: "main",
    title: "Contact LMSBOTS — CRM Support & Sales Assistance",
    description:
      "Contact LMSBOTS for CRM, lead management and WhatsApp automation. We reply within 24 hours. Email info@deetyasoft.com or call +91 90153 50505.",
    heading: "Contact LMSBOTS",
    summary: [
      "Talk to the LMSBOTS team about CRM, lead management and WhatsApp automation.",
    ],
    points: [
      "Email: info@deetyasoft.com (response within 24 hours)",
      "Phone: +91 90153 50505 (Mon–Sat, 9 am – 6 pm IST)",
      "Address: FF-06, First Floor, Mahaluxmi Square, Plot No. C-1, Abhay Khand-II, Indirapuram, Ghaziabad, Uttar Pradesh 201014, India",
    ],
    priority: "0.7",
    changefreq: "yearly",
  },
  "/solutions/healthcare": {
    name: "Healthcare CRM",
    group: "solution",
    title: "Healthcare CRM | Hospital Lead Management | LMSBOTS",
    description:
      "LMSBOTS Healthcare CRM helps hospitals capture patient inquiries, route them to the right department, automate follow-ups and raise appointment conversions.",
    heading: "Healthcare CRM & Patient Lead Management",
    summary: [
      "LMSBOTS Healthcare CRM helps hospitals and clinics capture patient inquiries from every channel, route them to the right department, automate follow-ups and increase appointment conversions.",
    ],
    points: [],
    priority: "0.8",
    changefreq: "monthly",
  },
  "/solutions/education": {
    name: "Education CRM",
    group: "solution",
    title: "Education CRM & Admission Lead Management | LMSBOTS",
    description:
      "LMSBOTS Education CRM helps colleges, coaching institutes and EdTech teams capture inquiries, route to counsellors, automate follow-ups and boost enrollments.",
    heading: "Education CRM & Admission Lead Management",
    summary: [
      "LMSBOTS Education CRM helps colleges, coaching institutes and EdTech teams capture student inquiries, route them to counsellors, automate follow-ups and fee reminders, and boost enrollments.",
    ],
    points: [],
    priority: "0.8",
    changefreq: "monthly",
  },
  "/solutions/real-estate": {
    name: "Real Estate CRM",
    group: "solution",
    title: "Real Estate CRM & Property Lead Management | LMSBOTS",
    description:
      "LMSBOTS real estate CRM helps agencies and builders capture property inquiries, assign leads, automate site-visit reminders and follow-ups, and close faster.",
    heading: "Real Estate CRM & Property Lead Management",
    summary: [
      "LMSBOTS real estate CRM helps agencies and builders capture property inquiries, score and assign leads, automate site-visit reminders and follow-ups, and close deals faster.",
    ],
    points: [],
    priority: "0.8",
    changefreq: "monthly",
  },
  "/solutions/insurance": {
    name: "Insurance CRM",
    group: "solution",
    title: "Insurance Lead Management System & CRM | LMSBOTS",
    description:
      "LMSBOTS insurance CRM helps insurers, brokers and agencies capture leads, route them to agents, automate quotes, renewals and claim updates, and sell more.",
    heading: "Insurance Lead Management System & CRM",
    summary: [
      "LMSBOTS insurance CRM helps insurers, brokers and agencies capture leads, score and route them to agents, automate quotes, renewals and claim updates, and convert more policies.",
    ],
    points: [],
    priority: "0.8",
    changefreq: "monthly",
  },
  "/solutions/banking-finance": {
    name: "Banking & Finance CRM",
    group: "solution",
    title: "Banking Lead Management System & Loan CRM | LMSBOTS",
    description:
      "LMSBOTS banking CRM helps banks, NBFCs and loan agencies capture loan leads, route them to RMs, automate KYC follow-ups and EMI reminders, and convert faster.",
    heading: "Banking & Finance Lead Management System",
    summary: [
      "LMSBOTS banking CRM helps banks, NBFCs and loan agencies capture loan leads, score and route them to relationship managers, automate KYC follow-ups and EMI reminders, and convert faster.",
    ],
    points: [],
    priority: "0.8",
    changefreq: "monthly",
  },
  "/solutions/technology": {
    name: "Technology",
    group: "solution",
    title: "AI-Powered Lead Management Technology | LMSBOTS",
    description:
      "Explore LMSBOTS AI lead management technology: lead scoring, omnichannel capture, workflow automation, real-time analytics and scalable cloud infrastructure.",
    heading: "AI-Powered Lead Management Technology",
    summary: [
      "LMSBOTS is an AI-powered lead management platform with intelligent lead scoring, omnichannel capture, workflow automation, real-time analytics and scalable cloud infrastructure.",
    ],
    points: [],
    priority: "0.8",
    changefreq: "monthly",
  },
  "/privacy-policy": {
    name: "Privacy Policy",
    group: "legal",
    title: "Privacy Policy | LMSBOTS CRM & Lead Management",
    description:
      "Read the LMSBOTS Privacy Policy: what data we collect, how it is used and kept, and the controls you have over your account and lead data.",
    heading: "Privacy Policy",
    summary: [
      "The LMSBOTS Privacy Policy explains what data we collect, how it is used, how long we keep it, and the controls you have over your account and lead data.",
    ],
    points: [],
    priority: "0.3",
    changefreq: "yearly",
  },
  "/terms-and-conditions": {
    name: "Terms & Conditions",
    group: "legal",
    title: "Terms & Conditions | LMSBOTS CRM & Lead Management",
    description:
      "LMSBOTS Terms & Conditions: service scope, eligibility, user responsibilities, billing and renewal, data security, liability and termination.",
    heading: "Terms & Conditions",
    summary: [
      "The LMSBOTS Terms & Conditions cover service scope, eligibility, user responsibilities, billing and renewal, data security, intellectual property, liability and termination.",
    ],
    points: [],
    priority: "0.3",
    changefreq: "yearly",
  },
};

/**
 * Routes that exist in the app but must NEVER be indexed
 * (auth screens, token pages, and the whole logged-in CRM).
 * Anything NOT in ROUTES is treated as non-indexable by the client-side guard.
 */
const NOINDEX_EXACT = ["/login", "/register", "/reset-password", "/oauth-callback", "/public-page", "/video-call"];

/* ------------------------------------------------------------------ */
/* Shared schema.org builders (one canonical Organization / WebSite)   */
/* ------------------------------------------------------------------ */

const ORG_ID = `${SITE_URL}/#organization`;
const WEBSITE_ID = `${SITE_URL}/#website`;
const SOFTWARE_ID = `${SITE_URL}/#software`;

const organizationSchema = (extra = {}) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: SITE.name,
  legalName: SITE.legalName,
  alternateName: ["LMSBots", "LMS Bots"],
  url: SITE_URL,
  slogan: SITE.tagline,
  logo: {
    "@type": "ImageObject",
    "@id": `${SITE_URL}/#logo`,
    url: `${SITE_URL}${SITE.logo}`,
    contentUrl: `${SITE_URL}${SITE.logo}`,
    width: 512,
    height: 512,
    caption: SITE.name,
  },
  image: `${SITE_URL}${SITE.ogImage}`,
  description:
    "LMSBOTS builds an AI lead management system and CRM that captures leads from every channel, assigns them automatically, automates follow-ups and tracks conversions.",
  foundingDate: "2020",
  email: SITE.email,
  telephone: SITE.telephone,
  address: { "@type": "PostalAddress", ...SITE.address },
  areaServed: SITE.areaServed.map((name) => ({ "@type": name === "India" ? "Country" : "City", name })),
  knowsAbout: [
    "Lead management system",
    "CRM software",
    "Sales automation",
    "WhatsApp CRM automation",
    "Real estate CRM",
    "Education CRM",
    "Healthcare CRM",
    "Insurance CRM",
    "Banking and loan CRM",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      telephone: SITE.telephone,
      email: SITE.email,
      areaServed: "IN",
      availableLanguage: ["English", "Hindi"],
      hoursAvailable: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:00",
        closes: "18:00",
      },
    },
  ],
  sameAs: SITE.sameAs,
  ...extra,
});

const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": WEBSITE_ID,
  url: SITE_URL,
  name: SITE.name,
  inLanguage: "en-IN",
  publisher: { "@id": ORG_ID },
});

const softwareSchema = (extra = {}) => ({
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "@id": SOFTWARE_ID,
  name: "LMSBOTS Lead Management System",
  alternateName: "LMSBOTS CRM",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "CRM / Lead Management",
  operatingSystem: "Web, Android, iOS",
  url: SITE_URL,
  image: `${SITE_URL}${SITE.ogImage}`,
  description: ROUTES["/"].description,
  publisher: { "@id": ORG_ID },
  offers: {
    "@type": "AggregateOffer",
    priceCurrency: "INR",
    lowPrice: "399",
    highPrice: "499",
    offerCount: 2,
    availability: "https://schema.org/InStock",
    url: `${SITE_URL}/pricing`,
  },
  featureList: [
    "Omnichannel lead capture",
    "Smart lead auto-assignment",
    "Automated email, SMS and WhatsApp follow-ups",
    "Centralized communication timeline",
    "Pipeline and ROI analytics",
    "Secure role-based access",
  ],
  ...extra,
});

const faqSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
});

const indexablePaths = () => Object.keys(ROUTES);

module.exports = {
  SITE_URL,
  SITE,
  FAQS,
  ROUTES,
  NOINDEX_EXACT,
  indexablePaths,
  ORG_ID,
  WEBSITE_ID,
  SOFTWARE_ID,
  organizationSchema,
  websiteSchema,
  softwareSchema,
  faqSchema,
};
